import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";

import { AuthGuard } from "./shared/guards/auth.guard";
import { UnauthGuard } from './shared/guards/unauth.guard';
import { ChangePwdGuard } from './shared/guards/change-pwd.guard';
import { PreventPartitionsChangesGuard } from "./shared/guards/prevent-partitions-changes.guard";
import { LicenceActiveGuard } from './shared/guards/licence-active.guard';

import { SimulationResolver } from "./shared/resolvers/simulation.resolver";
import { ParametersResolver } from "./shared/resolvers/parameters.resolver";
import { LicenceResolver } from "./shared/resolvers/licence.resolver";
import { LicencesResolver } from './shared/resolvers/licences.resolver';
import { DerogationResolver } from './shared/resolvers/derogation.resolver';
import { HelpResolver } from './shared/resolvers/help.resolver';
import { ReportSimuResolver } from './shared/resolvers/report-simu.resolver';
import { ReportCompareResolver } from './shared/resolvers/report-compare.resolver';
import { GroupingResolver } from './shared/resolvers/grouping.resolver';

import { LicenceRoleType } from './shared/models/enums/licence-role-type.enum';
import { ProviderType } from '@enums/provider-type.enum';

import { LoginComponent } from "./auth/login/login.component";
import { ForgotPasswordComponent } from "./auth/forgot-password/forgot-password.component";
import { ChangePasswordComponent } from "./auth/change-password/change-password.component";
import { HomeComponent } from "./home/home.component";
import { HelpComponent } from "./help/help.component";
import { ErrorComponent } from "./error/error.component";
import { MaintenanceComponent } from './help/maintenance/maintenance.component';
import { LicenceDetailComponent } from './licence/licence-detail/licence-detail.component';
import { LicencesListComponent } from './licence/licences-list/licences-list.component';
import { LicenceComponent } from './licence/licence.component';
import { SimulationComponent } from './simulation/simulation.component';
import { LiveComponent } from './simulation/live/live.component';
import { AdvancedComponent } from './simulation/advanced/advanced.component';
import { InvestComponent } from './simulation/invest/invest.component';
import { DerogationComponent } from './simulation/derogation/derogation.component';
import { ReportsComponent } from './reports/reports.component';
import { ReportSimuComponent } from './reports/report-simu/report-simu.component';
import { ReportCompareComponent } from './reports/report-compare/report-compare.component';
import { AccountComponent } from './user/account/account.component';
import { OriginSelectorComponent } from './parameters/origin-selector/origin-selector.component';
import { GroupingComponent } from './parameters/grouping/grouping.component';
import { ProviderFilesComponent } from './parameters/provider-files/provider-files.component';
import { SimulationFilesComponent } from './parameters/simulation-files/simulation-files.component';
import { UploadComponent } from './upload/upload.component';
import { ParametersComponent } from './parameters/parameters.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { CityDetailsComponent } from './dashboard/details/city-details/city-details.component';
import { OverviewComponent } from './dashboard/overview/overview.component';
import { AdvancedOverviewComponent } from './dashboard/advanced/advanced-overview.component';
import { DetailsComponent } from './dashboard/details/details.component';
import { TestsComponent } from './dashboard/details/tests/tests.component';
import { ErrorsComponent } from './dashboard/details/errors/errors.component';
import { LicenceBillingComponent } from "./licence/licence-billing/licence-billing.component";
import { ChangelogComponent } from "./help/changelog/changelog.component";
import { UnsubscribeNewsletterComponent } from "./auth/unsubscribe-newsletter/unsubscribe-newsletter.component";
import { ReportSystemComponent } from "./reports/report-system/report-system.component";

const routes: Routes = [
    {
        path: 'dashboardBizzDev', component: DashboardComponent,
        canActivateChild: [AuthGuard],
        data: {
            title: 'main.nav.links.dashboardBizzDev.title',
            roles: [
               LicenceRoleType.bzd
            ]
        },
        children: [
            {
                path: 'overview',
                component: OverviewComponent,
                data: {
                    title: 'main.nav.links.dashboardBizzDev.overview',
                    roles: [
                        LicenceRoleType.bzd
                    ]
                },
            },
            {
                path: 'advanced-overview',
                component: AdvancedOverviewComponent,
                data: {
                    title: 'main.nav.links.dashboardBizzDev.advancedOverview',
                    roles: [
                        LicenceRoleType.bzd
                    ]
                },
            },
            {
                path: 'details',
                component: DetailsComponent,
                children: [
                    {
                        path: 'cityDetails/:id',
                        component: CityDetailsComponent,
                        data: {
                            title: 'main.nav.links.dashboardBizzDev.cityDetails',
                            roles: [
                                LicenceRoleType.bzd
                            ]
                        }
                    },
                    {
                        path: 'testDetails',
                        component: TestsComponent,
                        data: {
                            title: 'main.nav.links.dashboardBizzDev.testDetails',
                            roles: [
                                LicenceRoleType.bzd
                            ]
                        }
                    },
                    {
                        path: 'errorDetail',
                        component:ErrorsComponent,
                        data: {
                            title: 'main.nav.links.dashboardBizzDev.errorDetail',
                            roles: [
                                LicenceRoleType.bzd
                            ]
                        }
                    }
                ]
            },
            { path: '**', redirectTo: 'overview', pathMatch: 'full' }
        ]
    },
    { path: 'helpdesk',
        loadChildren: () => import('./bizzmine/bizzmine.module').then(m => m.BizzmineModule),
    data: { title: 'main.nav.links.bizzmine' }
    },
    { path: 'wiki',
        loadChildren: () => import('./wiki/wiki.module').then(m => m.WikiModule),
        data: { title: 'main.nav.links.wiki' }
    },
    { path: 'help', component: HelpComponent,
        resolve: {isAuth: HelpResolver},
        data: { title: 'main.nav.links.help'}
    },
    { path: 'maintenance', component: MaintenanceComponent,
        data: { title: 'help.maintenance'}
    },
    { path: 'error', component: ErrorComponent,
        data: { title: 'error'}
    },
    { path: 'change-password', component: ChangePasswordComponent,
        canActivate: [ChangePwdGuard],
        data: { title: 'auth.changePassword.title'}
    },
    { path: 'welcome', component: ChangePasswordComponent,
        canActivate: [ChangePwdGuard],
        data: { title: 'auth.changePassword.title'}
    },
    { path: 'changelog', component: ChangelogComponent,
        data: { title: 'help.changelog.title'}
    },
    { path: 'unsubscribe', component: UnsubscribeNewsletterComponent,
        data: { title: 'auth.newsletter.unsubscribe'}
    },

    // Restricted: Unauthenticated
    { path: 'login', component: LoginComponent,
        canActivate: [UnauthGuard],
        data: { title: 'main.nav.links.login'}
    },
    { path: 'forgot-password', component: ForgotPasswordComponent,
        // canActivate: [UnauthGuard],
        data: { title: 'auth.forgotPassword'}
    },

    // Restricted: Authenticated user
    { path: 'home', component: HomeComponent,
        canActivate: [AuthGuard],
        data: { title: 'main.nav.links.home'}
    },
    { path: 'account', component: AccountComponent,
        canActivate: [AuthGuard],
        resolve: { userParameters: ParametersResolver },
        data: { title: 'main.nav.links.account'}
    },

    // Restricted: Role based restriction
    { path: 'licence', component: LicenceDetailComponent,
        resolve: { licence: LicenceResolver },
        canActivate: [AuthGuard],
        data: {
            title: 'main.nav.links.licence',
            roles: [
                LicenceRoleType.admin
            ]
        }
    },
    { path: 'licence', component: LicenceBillingComponent,
        resolve: { licence: LicenceResolver },
        canActivate: [AuthGuard],
        data: {
            title: 'main.nav.links.billing',
            roles: [
                LicenceRoleType.bzd,
                LicenceRoleType.bko
            ]
        }
    },
    { path: 'licences', component: LicenceComponent,
        canActivateChild: [AuthGuard],
        data: {
            title: 'main.nav.links.licences',
            roles: [
                LicenceRoleType.bzd,
                LicenceRoleType.bko,
                LicenceRoleType.servicing
            ]
        },
        children: [
            {
                path: 'billing',
                component: LicenceBillingComponent,
                resolve: { licence: LicenceResolver },
                data: {
                    title: 'main.nav.links.billing'
                }
            }, {
                path: ':code',
                component: LicenceDetailComponent,
                resolve: { licence: LicenceResolver },
                data: {
                    title: 'main.nav.links.licence'
                }
            }, {
                path: '',
                component: LicencesListComponent,
                resolve: { licences: LicencesResolver }
            }
        ]
    },
    { path: 'upload', component: UploadComponent,
        canActivate: [AuthGuard],
        data: {
            title: 'main.nav.links.upload',
            roles: [
                LicenceRoleType.bzd,
                LicenceRoleType.bko,
                LicenceRoleType.cfo,
                LicenceRoleType.admin,
                LicenceRoleType.bank
            ]
        }
    },

    // Restricted: Active licence + role based restriction
    { path: 'simulation', component: SimulationComponent,
        canActivateChild: [AuthGuard, LicenceActiveGuard],
        data: { title: 'main.nav.links.simulation.title',
            roles: [
                LicenceRoleType.man,
                LicenceRoleType.manBbc,
                LicenceRoleType.cfo,
                LicenceRoleType.bank,
                LicenceRoleType.admin,
                LicenceRoleType.bzd,
                LicenceRoleType.bko
            ]
        },
        children: [
            { path: 'live', component: LiveComponent,
                resolve: { simulation: SimulationResolver },
                data: { title: 'main.nav.links.simulation.live' }
            },
            { path: 'advanced', component: AdvancedComponent,
                canDeactivate: [PreventPartitionsChangesGuard],
                resolve: { simulation: SimulationResolver },
                data: { title: 'main.nav.links.simulation.advanced',
                    roles: [
                        LicenceRoleType.cfo,
                        LicenceRoleType.bank,
                        LicenceRoleType.admin,
                        LicenceRoleType.bzd,
                        LicenceRoleType.bko,
                        LicenceRoleType.manBbc
                    ]
                }
            },
            { path: 'investment', component: InvestComponent,
                data: { title: 'main.nav.links.simulation.invest',
                    roles: [
                        LicenceRoleType.cfo,
                        LicenceRoleType.manBbc,
                        LicenceRoleType.bank,
                        LicenceRoleType.admin,
                        LicenceRoleType.bzd,
                        LicenceRoleType.bko
                    ]
                }
            },
            { path: 'derogation', component: DerogationComponent,
                resolve: { derogations: DerogationResolver },
                data: { title: 'derogation.title',
                    roles: [
                        LicenceRoleType.cfo,
                        LicenceRoleType.admin,
                        LicenceRoleType.bzd,
                        LicenceRoleType.bko,
                        LicenceRoleType.bank
                    ]
                },
            },
            { path: '**', redirectTo: 'live', pathMatch: 'full' }
        ]
    },

    { path: 'parameters', component: ParametersComponent,
        canActivateChild: [AuthGuard, LicenceActiveGuard],
        data: { title: 'main.nav.links.parameters.title',
            roles: [
                LicenceRoleType.cfo,
                LicenceRoleType.bank,
                LicenceRoleType.admin,
                LicenceRoleType.bzd,
                LicenceRoleType.bko
            ]
        },
        children: [
            { path: 'data-select', component: OriginSelectorComponent,
                resolve: { userParameters: ParametersResolver },
                data: { title: 'main.nav.links.parameters.dataSelect',
                    roles: [
                        LicenceRoleType.bzd,
                        LicenceRoleType.bko,
                        LicenceRoleType.bank
                    ]
                }
            },
            { path: 'config-provider', component: ProviderFilesComponent,
                data: { title: 'main.nav.links.parameters.providerFiles',
                    providers: [
                        ProviderType.sic,
                        ProviderType.sicBxl,
                        ProviderType.bbc
                    ]
                }
            },
            { path: 'config-simulation', component: SimulationFilesComponent,
                data: { title: 'main.nav.links.parameters.simulationFiles' }
            },
            { path: 'grouping', component: GroupingComponent,
                resolve: { groups: GroupingResolver },
                data: { title: 'main.nav.links.parameters.grouping' }
            }
        ]
    },
    { path: 'reports', component: ReportsComponent,
        canActivateChild: [AuthGuard, LicenceActiveGuard],
        data: {
            title: 'main.nav.links.report.title',
            roles: [
                LicenceRoleType.cfo,
                LicenceRoleType.bank,
                LicenceRoleType.admin,
                LicenceRoleType.bzd,
                LicenceRoleType.bko
            ]
        },
        children: [
            { path: 'simulations', component: ReportSimuComponent,
                resolve: { simulations: ReportSimuResolver },
                data: { title: 'main.nav.links.report.simulation' }
            },
            { path: 'compare', component: ReportCompareComponent,
                resolve: { groups: ReportCompareResolver },
                data: { title: 'main.nav.links.report.compare' }
            },
            { path: 'system', component: ReportSystemComponent,
                resolve: { groups: ReportCompareResolver },
                data: {
                    title: 'main.nav.links.report.system',
                    roles: [LicenceRoleType.bzd, LicenceRoleType.bko, LicenceRoleType.bank]
                }
            },
            { path: '**', redirectTo: 'simulations', pathMatch: 'full' }
        ]
    },
    { path: '**', redirectTo: 'home', pathMatch: 'full' }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
    providers: []
})
export class AppRoutingModule {}
